// Gradient Builder - Color Stop Editor Component
// Add, remove, and edit color stops with hex input

import React, { useEffect, useCallback } from "react";
import { LuPlus, LuX } from "react-icons/lu";
import { ColorStop } from "../../../model/gradient";
import { colors, typography, effects } from "../../../design/tokens";

// Check if hex is complete (exactly 6 characters after #)
const isValidHex = (hex: string): boolean => {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
};

// Normalize incomplete hex codes to valid 6-character format
const normalizeHex = (hex: string): string => {
  let normalized = hex.toUpperCase();

  // Ensure # prefix
  if (!normalized.startsWith("#")) {
    normalized = "#" + normalized;
  }

  // Get hex part without #
  const hexPart = normalized.slice(1);

  if (hexPart.length === 0) {
    // Empty hex - revert to black
    return "#000000";
  } else if (hexPart.length < 6) {
    // Pad incomplete hex with last character (e.g., #F -> #FFFFFF, #AB -> #ABABAB)
    const padded = hexPart.padEnd(6, hexPart[hexPart.length - 1]);
    return "#" + padded;
  }

  return normalized;
};

interface ColorStopEditorProps {
  colorStops: ColorStop[];
  onChange: (colorStops: ColorStop[]) => void;
  onValidationChange?: (isValid: boolean) => void;
}

export const ColorStopEditor: React.FC<ColorStopEditorProps> = ({
  colorStops,
  onChange,
  onValidationChange,
}) => {
  // Track which color stops have invalid hex codes
  const invalidIndices = new Set<number>();
  colorStops.forEach((stop, index) => {
    if (!isValidHex(stop.color)) {
      invalidIndices.add(index);
    }
  });

  // Notify parent of validation state changes
  useEffect(() => {
    const allValid = colorStops.every(stop => isValidHex(stop.color));
    onValidationChange?.(allValid);
  }, [colorStops, onValidationChange]);

  const handleColorChange = useCallback((index: number, newColor: string) => {
    const updated = [...colorStops];
    updated[index] = { ...updated[index], color: newColor };
    onChange(updated);
  }, [colorStops, onChange]);

  const handleOffsetChange = useCallback((index: number, newOffset: number) => {
    // Clamp to 0-1 range
    const clampedOffset = Math.max(0, Math.min(1, newOffset));
    const updated = [...colorStops];
    updated[index] = { ...updated[index], offset: clampedOffset };
    // Sort by offset to maintain order
    updated.sort((a, b) => a.offset - b.offset);
    onChange(updated);
  }, [colorStops, onChange]);

  const handleAddStop = () => {
    if (colorStops.length >= 8) return;

    // Find largest gap between stops
    const sorted = [...colorStops].sort((a, b) => a.offset - b.offset);
    let maxGap = 0;
    let insertOffset = 0.5;

    for (let i = 0; i < sorted.length - 1; i++) {
      const gap = sorted[i + 1].offset - sorted[i].offset;
      if (gap > maxGap) {
        maxGap = gap;
        insertOffset = sorted[i].offset + gap / 2; // Middle of largest gap
      }
    }

    const newStop: ColorStop = {
      color: "#808080",
      offset: insertOffset,
    };

    const updated = [...colorStops, newStop].sort((a, b) => a.offset - b.offset);
    onChange(updated);
  };

  const handleRemoveStop = (index: number) => {
    if (colorStops.length <= 2) return;

    const updated = colorStops.filter((_, i) => i !== index);
    onChange(updated);
  };

  // Auto-normalize incomplete hex codes on blur
  const handleBlur = useCallback((index: number) => {
    const stop = colorStops[index];
    if (!isValidHex(stop.color)) {
      const normalized = normalizeHex(stop.color);
      handleColorChange(index, normalized);
    }
  }, [colorStops, handleColorChange]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className={`${typography.size.sm} ${colors.text.secondary} ${typography.weight.medium}`}>
          Colors ({colorStops.length}/8)
        </label>
        <button
          onClick={handleAddStop}
          disabled={colorStops.length >= 8}
          className={`${typography.size.sm} ${colors.text.secondary} hover:text-dark-text disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1`}
        >
          <LuPlus size={14} />
          Add Color
        </button>
      </div>

      {/* Color Stop List */}
      <div className="space-y-2">
        {colorStops.map((stop, index) => (
          <div
            key={index}
            className={`flex items-center gap-3 p-2 ${colors.surface.secondary} ${effects.borderRadius.lg} border ${colors.border.primary}`}
          >
            {/* Color Preview + Picker */}
            <div className="relative">
              <input
                type="color"
                value={isValidHex(stop.color) ? stop.color : normalizeHex(stop.color)}
                onChange={(e) => handleColorChange(index, e.target.value)}
                className={`w-10 h-10 ${effects.borderRadius.default} cursor-pointer border ${colors.border.primary}`}
              />
            </div>

            {/* Hex Input */}
            <input
              type="text"
              value={stop.color}
              onChange={(e) => {
                const value = e.target.value;
                if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                  handleColorChange(index, value);
                }
              }}
              onBlur={() => handleBlur(index)}
              placeholder="#000000"
              className={`flex-1 px-3 py-2 ${colors.surface.primary} ${colors.text.primary} ${typography.size.sm} ${effects.borderRadius.default} border ${
                invalidIndices.has(index)
                  ? "border-yellow-500 ring-1 ring-yellow-500/30"
                  : `${colors.border.primary} focus:border-primary-blue`
              } focus:outline-none`}
              maxLength={7}
            />

            {/* Position Input */}
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                max="100"
                value={Math.round(stop.offset * 100)}
                onChange={(e) => handleOffsetChange(index, parseInt(e.target.value || "0") / 100)}
                className={`w-12 px-1 py-1 ${colors.surface.primary} ${colors.text.primary} ${typography.size.xs} text-center ${effects.borderRadius.default} border ${colors.border.primary} focus:border-primary-blue focus:outline-none`}
              />
              <span className={`${typography.size.xs} ${colors.text.secondary}`}>%</span>
            </div>

            {/* Remove Button */}
            <button
              onClick={() => handleRemoveStop(index)}
              disabled={colorStops.length <= 2}
              className={`${colors.text.secondary} hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
            >
              <LuX size={18} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
